// Compose sheet: pick stationery, write, attach - while a live
// ghost of the note sits on the board above the sheet, updating
// with every choice. "Pin it" makes the ghost real.

import type { BoardConfig, Note, NoteUpdate } from "@ts/core/types.ts";
import { FONTS, INKS, PAPERS, PIN_COLORS, inkColor } from "@ts/core/types.ts";
import { TRANSLIT_ITC, finishedWord, trailingWord, transliterateWord } from "@ts/core/translit.ts";
import type { MediaChange, NewNote } from "@ts/store/store.ts";
import type { GhostData } from "../board/world.ts";
import { $, el } from "../util/dom.ts";
import { resizeImage } from "../media/photo.ts";
import { toggleRecording, voiceSupported } from "../media/record.ts";
import { dictationSupported, toggleDictation } from "../media/dictate.ts";

export interface ComposeCtx {
  cfg: BoardConfig;
  isFrozen(): boolean;
  atCap(): boolean;
  /** Where a new note will land (world coords). */
  nextPosition(): { x: number; y: number };
  onGhost(ghost: GhostData | null): void;
  submitNew(note: NewNote, media: MediaChange): Promise<void>;
  submitEdit(note: Note, token: string | null, fields: NoteUpdate, media: MediaChange): Promise<void>;
  deleteNote(note: Note, token: string | null): Promise<void>;
  track?(name: string, props?: Record<string, unknown>): void;
}

export interface ComposeHandle {
  open(): void;
  openEdit(note: Note, token: string | null): void;
  isOpen(): boolean;
  /** id of the note currently open for editing, if any. */
  editingId(): string | null;
}

export function initCompose(ctx: ComposeCtx): ComposeHandle {
  const chosen = { paper: "cream", font: "caveat", ink: "graphite" };
  let photoBlob: Blob | null = null;
  let audioBlob: Blob | null = null;
  let editing: { note: Note; token: string | null } | null = null;
  let removePhoto = false;
  let removeAudio = false;
  let previewRotation = -1.5;
  let previewPos = { x: 0, y: 0 };

  const sheet = $("sheet");
  const textEl = $<HTMLTextAreaElement>("note-text");
  const nameEl = $<HTMLInputElement>("note-name");
  const emailEl = $<HTMLInputElement>("note-email");
  const swatchBtns: Record<string, Record<string, HTMLButtonElement>> = {
    paper: {},
    font: {},
    ink: {},
  };

  // ── ghost ──
  function ghost(): GhostData {
    return {
      message: textEl.value.trim(),
      name: nameEl.value.trim() || null,
      paper: chosen.paper,
      font: chosen.font,
      ink: chosen.ink,
      rotation: previewRotation,
      pos_x: previewPos.x,
      pos_y: previewPos.y,
      photo_url: photoBlob
        ? URL.createObjectURL(photoBlob)
        : editing && !removePhoto
          ? editing.note.photo_url
          : null,
      audio_url: audioBlob
        ? "local"
        : editing && !removeAudio
          ? editing.note.audio_url
          : null,
    };
  }
  const syncGhost = () => ctx.onGhost(ghost());

  // ── swatches ──
  function buildSwatches(
    containerId: string,
    items: ReadonlyArray<{ id: string; label?: string; glyph?: string; color?: string }>,
    key: "paper" | "font" | "ink",
    render: (b: HTMLButtonElement, item: any) => void
  ) {
    const box = $(containerId);
    items.forEach((item, i) => {
      const b = el("button", "swatch");
      b.type = "button";
      b.setAttribute("role", "radio");
      b.setAttribute("aria-checked", String(i === 0));
      b.setAttribute("aria-label", item.label || item.id);
      b.title = item.label || item.id;
      render(b, item);
      b.onclick = () => {
        chosen[key] = item.id;
        [...box.children].forEach((c) => c.setAttribute("aria-checked", "false"));
        b.setAttribute("aria-checked", "true");
        if (item.id === "kavivanar") $<HTMLSelectElement>("lang-select").value = "ta";
        if (item.id === "chilanka") $<HTMLSelectElement>("lang-select").value = "ml";
        stylePaper();
        syncGhost();
      };
      swatchBtns[key][item.id] = b;
      box.appendChild(b);
    });
  }
  const selectSwatch = (key: "paper" | "font" | "ink", id: string) => swatchBtns[key][id]?.click();

  buildSwatches("paper-swatches", PAPERS, "paper", (b, item) => b.classList.add(`paper-${item.id}`));
  buildSwatches("font-swatches", FONTS, "font", (b, item) => {
    b.classList.add(`font-${item.id}`);
    b.textContent = item.glyph || "Aa";
  });
  buildSwatches("ink-swatches", INKS, "ink", (b, item) => (b.style.background = item.color));

  function stylePaper() {
    const paper = $("compose-paper");
    paper.className = `compose-paper paper-${chosen.paper} font-${chosen.font}`;
    paper.style.color = inkColor(chosen.ink);
  }
  stylePaper();

  // ── text, count, transliteration ──
  const countEl = $("char-count");
  const updateCount = () => {
    countEl.textContent = `${textEl.value.length} / ${ctx.cfg.maxChars}`;
    countEl.classList.toggle("over", textEl.value.length > ctx.cfg.maxChars);
  };
  textEl.addEventListener("input", () => {
    updateCount();
    syncGhost();
  });
  nameEl.addEventListener("input", syncGhost);
  updateCount();

  // Applies to the note AND the signature - names deserve their own
  // script (தமிழ் / മലയാളം) just as much as the message does.
  function attachTransliteration(
    field: HTMLTextAreaElement | HTMLInputElement,
    after: () => void
  ) {
    field.addEventListener("input", async (e) => {
      if (!(e as InputEvent).isTrusted) return;
      const itc = TRANSLIT_ITC[$<HTMLSelectElement>("lang-select").value];
      if (!itc) return;
      const m = finishedWord(field.value);
      if (!m || m.index === undefined) return;
      const start = m.index;
      const out = await transliterateWord(m[1], itc);
      if (out === m[1]) return;
      if (field.value.slice(start, start + m[0].length) !== m[0]) return;
      const atEnd = field.selectionStart === field.value.length;
      field.value =
        field.value.slice(0, start) + out + m[2] + field.value.slice(start + m[0].length);
      if (atEnd) field.setSelectionRange(field.value.length, field.value.length);
      after();
    });
  }
  attachTransliteration(textEl, () => {
    updateCount();
    syncGhost();
  });
  attachTransliteration(nameEl, syncGhost);

  async function flushTransliteration() {
    const itc = TRANSLIT_ITC[$<HTMLSelectElement>("lang-select").value];
    if (!itc) return;
    for (const field of [textEl, nameEl]) {
      const m = trailingWord(field.value);
      if (m && m.index !== undefined) {
        field.value = field.value.slice(0, m.index) + (await transliterateWord(m[1], itc));
      }
    }
  }

  // ── attachments ──
  const previewBox = $("attach-preview");
  function renderAttachPreview() {
    previewBox.innerHTML = "";
    const photoSrc = photoBlob
      ? URL.createObjectURL(photoBlob)
      : editing && !removePhoto
        ? editing.note.photo_url
        : null;
    const audioSrc = audioBlob
      ? URL.createObjectURL(audioBlob)
      : editing && !removeAudio
        ? editing.note.audio_url
        : null;
    previewBox.hidden = !photoSrc && !audioSrc;

    if (photoSrc) {
      const img = el("img");
      img.src = photoSrc;
      img.alt = "photo preview";
      img.draggable = false;
      previewBox.appendChild(img);
      previewBox.appendChild(
        removeBtn("remove photo", () => {
          if (photoBlob) photoBlob = null;
          else removePhoto = true;
          $<HTMLInputElement>("photo-input").value = "";
          renderAttachPreview();
          syncGhost();
        })
      );
    }
    if (audioSrc) {
      const audio = el("audio");
      audio.controls = true;
      audio.src = audioSrc as string;
      previewBox.appendChild(audio);
      previewBox.appendChild(
        removeBtn("remove voice note", () => {
          if (audioBlob) audioBlob = null;
          else removeAudio = true;
          renderAttachPreview();
          syncGhost();
        })
      );
    }
  }
  function removeBtn(label: string, onClick: () => void): HTMLButtonElement {
    const b = el("button", "attach-remove", `× ${label}`);
    b.type = "button";
    b.onclick = onClick;
    return b;
  }

  $<HTMLInputElement>("photo-input").addEventListener("change", async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    photoBlob = await resizeImage(file);
    removePhoto = false;
    renderAttachPreview();
    syncGhost();
    ctx.track?.("photo_added");
  });

  // Mic APIs need a secure context: they exist on https and on
  // localhost, but NOT on a plain http LAN address (phone testing).
  // Explain instead of silently hiding the buttons.
  const insecure = !window.isSecureContext;
  const micUnavailable = () =>
    showError(
      insecure
        ? "recording needs a secure connection - it works on the real (https) link, just not on this http preview"
        : "this browser can't record audio - try Chrome or Safari"
    );

  if (!voiceSupported) {
    $("voice-btn").classList.add("unavailable");
    $("voice-btn").onclick = micUnavailable;
  } else {
    $("voice-btn").onclick = async () => {
      try {
        await toggleRecording({
          button: $<HTMLButtonElement>("voice-btn"),
          onDone(blob) {
            audioBlob = blob;
            removeAudio = false;
            renderAttachPreview();
            syncGhost();
            ctx.track?.("voice_recorded", { bytes: blob.size });
          },
        });
      } catch {
        showError("couldn't access the microphone - you can still leave a written note");
      }
    };
  }

  if (!dictationSupported) {
    $("dictate-btn").classList.add("unavailable");
    $("dictate-btn").onclick = () =>
      showError(
        insecure
          ? "dictation needs a secure (https) connection - it works on the real link"
          : "dictation isn't available in this browser - it works best in Chrome or Edge"
      );
  } else $("dictate-btn").onclick = () => {
    ctx.track?.("dictation_used", { lang: $<HTMLSelectElement>("lang-select").value });
    toggleDictation({
      button: $<HTMLButtonElement>("dictate-btn"),
      getLang: () => $<HTMLSelectElement>("lang-select").value,
      onText(text) {
        textEl.value = (textEl.value ? textEl.value.trimEnd() + " " : "") + text;
        updateCount();
        syncGhost();
      },
      onStatus(msg, isError) {
        const s = $("dictate-status");
        s.hidden = !msg;
        s.textContent = msg;
        s.classList.toggle("err", Boolean(isError));
      },
    });
  };

  // ── open / close / submit ──
  function showError(msg: string) {
    const e = $("compose-error");
    e.textContent = msg;
    e.hidden = false;
  }

  function resetForm() {
    textEl.value = "";
    nameEl.value = "";
    emailEl.value = "";
    $<HTMLInputElement>("photo-input").value = "";
    photoBlob = audioBlob = null;
    removePhoto = removeAudio = false;
    editing = null;
    $("compose-error").hidden = true;
    $("pin-btn").textContent = "pin it to the board";
    $("delete-btn").hidden = true;
    $("email-row").hidden = false;
    renderAttachPreview();
    updateCount();
  }

  function openSheet() {
    sheet.hidden = false;
    syncGhost();
  }

  function close() {
    sheet.hidden = true;
    ctx.onGhost(null);
    resetForm();
  }
  $("compose-close").onclick = close;

  // gentle nudges for people with starting trouble
  const PROMPTS = [
    "what did the evening feel like?",
    "introduce yourself, slam-book style - 'im the one who…'",
    "a moment from the room you don't want to forget?",
    "what did Old Friend sound like, sitting in that room?",
    "say it like you'd write it in a friend's slam book",
  ];
  let promptIdx = 0;

  function open() {
    resetForm();
    textEl.placeholder = PROMPTS[promptIdx++ % PROMPTS.length];
    previewRotation = +(Math.random() * 8 - 4).toFixed(2);
    previewPos = ctx.nextPosition();
    openSheet();
  }

  function openEdit(note: Note, token: string | null) {
    resetForm();
    editing = { note, token };
    previewRotation = note.rotation;
    previewPos = { x: note.pos_x ?? 0, y: note.pos_y ?? 0 };
    selectSwatch("paper", note.paper);
    selectSwatch("font", note.font);
    selectSwatch("ink", note.ink);
    textEl.value = note.message || "";
    nameEl.value = note.name || "";
    updateCount();
    $("pin-btn").textContent = "update your note";
    $("delete-btn").hidden = false;
    $("email-row").hidden = true;
    renderAttachPreview();
    openSheet();
  }

  $("delete-btn").onclick = async () => {
    if (!editing) return;
    if (!confirm("Unpin this note? You can write a new one after.")) return;
    try {
      await ctx.deleteNote(editing.note, editing.token);
      close();
    } catch {
      showError("couldn't remove the note - try again in a moment");
    }
  };

  $("pin-btn").onclick = async () => {
    await flushTransliteration().catch(() => {});
    updateCount();
    const message = textEl.value.trim();
    $("compose-error").hidden = true;

    const keepsPhoto = photoBlob || (editing && editing.note.photo_url && !removePhoto);
    const keepsAudio = audioBlob || (editing && editing.note.audio_url && !removeAudio);
    if (!message && !keepsPhoto && !keepsAudio) return showError("write a little something first");
    if (message.length > ctx.cfg.maxChars)
      return showError(`keep it note-sized - under ${ctx.cfg.maxChars} characters`);
    if (ctx.isFrozen())
      return showError("the board has just frozen - thank you for being there 🤍");
    if (!editing && ctx.atCap())
      return showError(
        `you've pinned your ${ctx.cfg.maxNotesPerPerson} notes - remove one to write again`
      );

    const btn = $<HTMLButtonElement>("pin-btn");
    btn.disabled = true;
    btn.textContent = editing ? "updating…" : "pinning…";
    const media: MediaChange = { photo: photoBlob, audio: audioBlob, removePhoto, removeAudio };

    try {
      if (editing) {
        await ctx.submitEdit(
          editing.note,
          editing.token,
          {
            message,
            name: nameEl.value.trim(),
            paper: chosen.paper,
            font: chosen.font,
            ink: chosen.ink,
          },
          media
        );
      } else {
        await ctx.submitNew(
          {
            message,
            name: nameEl.value.trim() || null,
            paper: chosen.paper,
            font: chosen.font,
            ink: chosen.ink,
            rotation: previewRotation,
            pin: PIN_COLORS[Math.floor(Math.random() * PIN_COLORS.length)],
            pos_x: previewPos.x,
            pos_y: previewPos.y,
          },
          media
        );
      }
      close();
    } catch (e: any) {
      console.error(e);
      showError(e?.friendly ?? "the note didn't stick - check your connection and try again");
    } finally {
      btn.disabled = false;
      btn.textContent = editing ? "update your note" : "pin it to the board";
    }
  };

  return {
    open,
    openEdit,
    isOpen: () => !sheet.hidden,
    editingId: () => (sheet.hidden ? null : (editing?.note.id ?? null)),
  };
}
